import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import Copyable from '../../../components/utils/Copyable';
import TopBar from '../../../components/utils/TopBar';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import NA from '../../../components/utils/NA';

import ErrorLogsDetailsModal from './ErrorLogsDetailsModal';

import {
  GetErrorLogsBIZ,
  GetErrorLogsStatsBIZ,
  DeleteErrorLogBIZ,
} from '../../../redux/actions/business/errorLogsActions';

const SEVERITY_OPTIONS = ['CRITICAL', 'ERROR', 'WARNING', 'INFO'];
const ENVIRONMENT_OPTIONS = ['production', 'alpha', 'local'];
const TIME_RANGE_OPTIONS = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
];

// accents that read well on the app's dark surfaces
const SEVERITY_ACCENT = {
  CRITICAL: '#f87171',
  ERROR: '#fb923c',
  WARNING: '#fbbf24',
  INFO: '#60a5fa',
};

const TILE_LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';

const ErrorLogsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state.business);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [statsLoading, setStatsLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedErrorLog, setSelectedErrorLog] = useState(null);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsId, setDetailsId] = useState(null);

  // filters
  const [severityFilter, setSeverityFilter] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState('');
  const [resolvedFilter, setResolvedFilter] = useState('');
  const [timeRange, setTimeRange] = useState('24h');

  const [errorLogsList, setErrorLogsList] = useState([]);
  const [errorLogsTotal, setErrorLogsTotal] = useState(0);
  const [stats, setStats] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    {
      label: 'ID',
      sort: 'id',
    },
    {
      label: 'Severity',
      sort: 'severity',
    },
    {
      label: 'Application',
      sort: 'application_name',
    },
    {
      label: 'Environment',
      sort: 'environment',
    },
    {
      label: 'Error Message',
      sort: null,
    },
    {
      label: 'Origin',
      sort: 'function_name',
    },
    {
      label: 'Endpoint',
      sort: null,
    },
    {
      label: 'Status',
      sort: 'is_resolved',
    },
    {
      label: 'Timestamps',
      sort: 'createdAt',
    },
    {
      label: 'Actions',
      sort: null,
    },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSort(value);
  };

  // narrowing the result set while sitting on a later page would show an empty
  // table, so every filter change goes back to the first page
  const applyFilter = (setter) => (e) => {
    setter(e.target.value);
    handleChangePage(null, 0);
  };

  useEffect(() => {
    handleGetErrorLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection, severityFilter, environmentFilter, resolvedFilter]);

  useEffect(() => {
    handleGetStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  useEffect(() => {
    try {
      setErrorLogsList(state.errorLogsList?.records || []);
      setErrorLogsTotal(state.errorLogsList?.total || 0);
      setStats(state.errorStats || null);
    } catch (err) {}
  }, [state]);

  async function handleGetErrorLogs(customLoading) {
    let query = '';
    if (search) query += `&search=${search}`;
    if (sort) query += `&field=${sort}`;
    if (sortDirection) query += `&order=${sortDirection}`;
    if (severityFilter) query += `&severity=${severityFilter}`;
    if (environmentFilter) query += `&environment=${environmentFilter}`;
    if (resolvedFilter) query += `&is_resolved=${resolvedFilter}`;
    await dispatch(GetErrorLogsBIZ(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  }

  async function handleGetStats() {
    await dispatch(GetErrorLogsStatsBIZ(`?time_range=${timeRange}`, setStatsLoading));
  }

  async function handleRefresh() {
    await handleGetErrorLogs();
    await handleGetStats();
  }

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedErrorLog(null);
  };

  const handleDelete = async () => {
    await dispatch(DeleteErrorLogBIZ(selectedErrorLog.id, setDeleteLoading));
    handleCloseDeleteModal();
    await handleRefresh();
  };

  const getStatusColor = (isResolved) => {
    return isResolved ? 'success' : 'error';
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL':
        return 'error';
      case 'ERROR':
        return 'error';
      case 'WARNING':
        return 'warning';
      case 'INFO':
        return 'info';
      default:
        return 'default';
    }
  };

  const getEnvironmentColor = (environment) => {
    switch (environment?.toUpperCase()) {
      case 'PRODUCTION':
        return 'error';
      case 'ALPHA':
        return 'info';
      case 'LOCAL':
        return 'success';
      default:
        return 'default';
    }
  };

  // by_severity comes back as [{ severity, count }] with count as a string
  const severityCount = (severity) => {
    const entry = (stats?.by_severity || []).find(
      (s) => (s.severity || '').toUpperCase() === severity
    );
    return Number(entry?.count || 0);
  };

  const summaryTiles = [
    { label: `Total (${timeRange})`, value: Number(stats?.total_errors || 0), accent: '#e2f2f9' },
    { label: 'Unresolved', value: Number(stats?.unresolved_errors || 0), accent: '#f87171' },
    { label: 'Resolved', value: Number(stats?.resolved_errors || 0), accent: '#4ade80' },
    ...SEVERITY_OPTIONS.map((s) => ({
      label: s.charAt(0) + s.slice(1).toLowerCase(),
      value: severityCount(s),
      accent: SEVERITY_ACCENT[s],
    })),
  ];

  return (
    <Box>
      <ConfirmationModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title="Delete error log"
        message="This permanently removes the log entry. This cannot be undone."
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={handleCloseDeleteModal}
      />

      <ErrorLogsDetailsModal
        open={detailsModalOpen}
        setOpen={setDetailsModalOpen}
        errorLogId={detailsId}
        severityColor={getSeverityColor}
        environmentColor={getEnvironmentColor}
        handleGet={handleRefresh}
        />

      <TopBar
        title={'Error Logs'}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button1Text={'Refresh'}
        button1OnClick={() => {
          handleRefresh();
        }}
        />

      {/* ---------- summary tiles ---------- */}
      <Box
        display={'flex'}
        flexWrap={'wrap'}
        gap={'12px'}
        mb={'14px'}>
        {
          summaryTiles.map((tile, i) => (
            <Box
              key={i}
              sx={{
                flex: '1 1 130px',
                minWidth: '130px',
                border: `1px solid ${tile.accent}40`,
                background: `${tile.accent}14`,
                borderRadius: '12px',
                padding: '14px 16px',
              }}>
              <Typography
                sx={{ fontSize: '26px', fontWeight: 700, lineHeight: 1.1, color: tile.accent }}>
                {tile.value}
              </Typography>
              <Typography
                variant='caption'
                sx={{ color: TILE_LABEL_COLOR }}>
                {tile.label}
              </Typography>
            </Box>
          ))
        }
      </Box>

      {/* ---------- filters ---------- */}
      <Box
        className='flexCenter_Row'
        flexWrap={'wrap'}
        gap={'12px'}
        mb={'14px'}>
        <TextField
          select
          size='small'
          label='Severity'
          sx={{ minWidth: '150px' }}
          value={severityFilter}
          onChange={applyFilter(setSeverityFilter)}>
          <MenuItem value=''>All</MenuItem>
          {SEVERITY_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size='small'
          label='Environment'
          sx={{ minWidth: '150px' }}
          value={environmentFilter}
          onChange={applyFilter(setEnvironmentFilter)}>
          <MenuItem value=''>All</MenuItem>
          {ENVIRONMENT_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size='small'
          label='Status'
          sx={{ minWidth: '150px' }}
          value={resolvedFilter}
          onChange={applyFilter(setResolvedFilter)}>
          <MenuItem value=''>All</MenuItem>
          <MenuItem value='false'>Pending</MenuItem>
          <MenuItem value='true'>Resolved</MenuItem>
        </TextField>

        <Box flexGrow={1} />

        <ToggleButtonGroup
          size='small'
          exclusive
          value={timeRange}
          onChange={(e, value) => {
            if (value) setTimeRange(value);
          }}>
          {TIME_RANGE_OPTIONS.map((t) => (
            <ToggleButton key={t.value} value={t.value}>{t.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box height={'300px'} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
      ) : errorLogsList.length === 0 ? (
        <Box height={'300px'} className="flexCenterCenterRow">
          <Typography>No error logs found</Typography>
        </Box>
      ) : (
        <Table className="block">
          <TableHead>
            <TableRow>
              {tableHeadingsList.map((heading, headingIndex) =>
                heading.sort === null ? (
                  <TableCell key={headingIndex} align={heading.align || 'left'}>
                    {heading.label}
                  </TableCell>
                ) : (
                  <TableCell key={headingIndex} align={heading.align || 'left'}>
                    <TableSortLabel
                      active={sort === heading.sort ? true : false}
                      direction={sortDirection}
                      onClick={() => {
                        handleSort(heading.sort);
                      }}
                    >
                      {heading.label}
                    </TableSortLabel>
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {errorLogsList.map((row, index) => (
              <TableRow
                key={index}
                className="table__clickable-row"
                onClick={() => {
                  setDetailsId(row.id);
                  setDetailsModalOpen(true);
                }}
                sx={{
                  // a coloured left edge makes severity scannable without
                  // reading the chip on every row
                  borderLeft: `3px solid ${SEVERITY_ACCENT[row.severity?.toUpperCase()] || 'transparent'}`,
                }}>
                <TableCell>
                  <Typography
                    variant="caption"
                    color={"grey"}>
                    <Copyable>
                      {row.id || <NA />}
                    </Copyable>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.severity || 'N/A'}
                    color={getSeverityColor(row.severity)}
                    variant="filled"
                  />
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                    {row.application_name || <NA />}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.environment || 'N/A'}
                    color={getEnvironmentColor(row.environment)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={row.error_message || ''}>
                    <Typography
                      sx={{
                        maxWidth: '320px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.error_message || <NA />}
                    </Typography>
                  </Tooltip>
                  {
                    row.error_code
                    &&
                    <Typography variant='caption' color={"grey"}>
                      {row.error_code}
                    </Typography>
                  }
                </TableCell>
                <TableCell>
                  <Tooltip title={row.file_path || ''}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.function_name || <NA />}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box className='flexCenter_Row' gap={'8px'}>
                    <Chip
                      size="small"
                      label={row.request_method || 'N/A'}
                      color="primary"
                      variant="outlined"
                    />
                    <Tooltip title={row.endpoint || ''}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          maxWidth: '220px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.endpoint || <NA />}
                      </Typography>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    icon={row.is_resolved ? <CheckCircleOutlineIcon /> : undefined}
                    label={row.is_resolved ? 'Resolved' : 'Pending'}
                    color={getStatusColor(row.is_resolved)}
                    variant="filled"
                  />
                </TableCell>
                <TableCell>
                  <TableTimeStamps
                    createdAt={row.createdAt}
                    updatedAt={row.updatedAt}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedErrorLog(row);
                        setDeleteModalOpen(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                sx={{ borderBottom: 0 }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                colSpan={10}
                count={errorLogsTotal}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </Box>
  );
};

export default ErrorLogsPage;
